import React, { FC } from 'react';
import {
    TextField, Grid, FormControl,
    InputLabel, Select, MenuItem, Box,
    InputAdornment,
    FormHelperText
} from '@mui/material';
import { TFunction } from 'i18next';
import { nameValidation, normalizeString } from 'utils/Validations';
import { TipoAcumulacionEnum } from 'repository/timekeeper/@types/TypeAccrualEnum';
import { TipoCascadaEnum } from 'repository/timekeeper/@types/TypeCascadeEnum';
import { CascadeOrderList } from './CascadeOrderList';
import { AccrualCode } from 'repository/timekeeper/@types/AccrualCode';
import { DatePattern } from 'repository/timekeeper/@types/DatePattern';
import { BalanceDomino, BalanceDominoCascadeItem } from 'repository/timekeeper/@types/BalanceDomino';

interface BalanceDominoFormData {
    balance_domino_id?: number;
    name: string;
    description: string | null;
    date_pattern_id?: number;
    type_accrual: TipoAcumulacionEnum;
    accrual_code_id?: number | null;
    fixed_amount: number;
    percentage_cascade: number;
    type_cascade: TipoCascadaEnum;
    cascade_orders?: BalanceDominoCascadeItem[];
}

interface BalanceDominoFormProps {
    data: BalanceDominoFormData;
    setData: (data: BalanceDominoFormData) => void;
    rows: BalanceDomino[];
    rowToEdit?: BalanceDomino;
    t: TFunction;
    datePatterns?: DatePattern[];
    accrualCodes?: AccrualCode[];
    mode: 'create' | 'update';
}

export const BalanceDominoForm: FC<BalanceDominoFormProps> = ({
    data,
    setData,
    rows,
    rowToEdit,
    t,
    datePatterns = [],
    accrualCodes = [],
    mode
}) => {

    const existName = (name: string) => {
        const normalizedInput = normalizeString(name);
        const currentId = rowToEdit?.balance_domino_id;

        if (!Array.isArray(rows)) return false;

        const existsInRows = rows.some((row: any) =>
            normalizeString(row?.name || '') === normalizedInput &&
            (mode === 'update' ? row?.balance_domino_id !== currentId : true)
        );

        return existsInRows;
    };

    return (
        <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
            {/* Nombre */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <TextField
                    fullWidth
                    name="Name"
                    variant='outlined'
                    label={t("accruals.balance_domino.dialog.inputs.name")}
                    value={data.name}
                    autoComplete='off'
                    onChange={(e) => {
                        if (nameValidation(e.target.value)) {
                            setData({ ...data, name: e.target.value });
                        }
                    }}
                    error={existName(data.name) && data.name !== ''}
                    slotProps={{
                        htmlInput: { maxLength: 100 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${data.name.length}/100`}</InputAdornment>,
                        }
                    }}
                />
                <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }} component={'span'}>
                    <Box component={'span'} color={(theme) => theme.palette.error.main}>{existName(data.name) && data.name !== '' ? t('accruals.balance_domino.dialog.alerts.name_exist') : ''}</Box>
                </FormHelperText>
            </Grid>

            {/* Descripción */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    name="Description"
                    label={t("accruals.balance_domino.dialog.inputs.description")}
                    value={data.description || ''}
                    autoComplete='off'
                    onChange={(e) => {
                        setData({ ...data, description: e.target.value || null });
                    }}
                    slotProps={{
                        htmlInput: { maxLength: 255 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${data.description?.length || 0}/255`}</InputAdornment>,
                        }
                    }}
                />
            </Grid>

            {/* Patrón de fechas */}
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                <FormControl fullWidth>
                    <InputLabel>{t("accruals.balance_domino.dialog.inputs.date_pattern")}</InputLabel>
                    <Select
                        value={data.date_pattern_id || ''}
                        label={t("accruals.balance_domino.dialog.inputs.date_pattern")}
                        onChange={(e) => {
                            setData({ ...data, date_pattern_id: e.target.value as number });
                        }}
                    >
                        {datePatterns.map((pattern) => (
                            <MenuItem key={pattern.date_pattern_id} value={pattern.date_pattern_id}>
                                {pattern.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {/* Tipo de acumulación */}
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                <FormControl fullWidth>
                    <InputLabel>{t("accruals.balance_domino.dialog.inputs.type_accrual")}</InputLabel>
                    <Select
                        value={data.type_accrual}
                        label={t("accruals.balance_domino.dialog.inputs.type_accrual")}
                        onChange={(e) => {
                            setData({ ...data, type_accrual: e.target.value as TipoAcumulacionEnum });
                        }}
                    >
                        <MenuItem value={TipoAcumulacionEnum.HORAS}>{t("accruals.balance_domino.dialog.inputs.type_accrual_options.hours")}</MenuItem>
                        <MenuItem value={TipoAcumulacionEnum.DIAS}>{t("accruals.balance_domino.dialog.inputs.type_accrual_options.days")}</MenuItem>
                        <MenuItem value={TipoAcumulacionEnum.DINERO}>{t("accruals.balance_domino.dialog.inputs.type_accrual_options.money")}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            {/* Sección: Cantidad de balances con efecto dominó */}
            <Grid size={{ xs: 12 }}>
                <Box sx={{ mt: 3, mb: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
                    <strong>{t("accruals.balance_domino.dialog.sections.domino_balance_amount")}</strong>
                </Box>
            </Grid>

            {/* Código de acumulación */}
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <FormControl fullWidth>
                    <InputLabel>{t("accruals.balance_domino.dialog.inputs.accrual_code")}</InputLabel>
                    <Select
                        value={data.accrual_code_id || ''}
                        label={t("accruals.balance_domino.dialog.inputs.accrual_code")}
                        onChange={(e) => {
                            setData({ ...data, accrual_code_id: e.target.value ? Number(e.target.value) : null });
                        }}
                    >
                        <MenuItem value="">
                            <em>{t("general.basic.none")}</em>
                        </MenuItem>
                        {accrualCodes.map((code) => (
                            <MenuItem key={code.accrual_code_id} value={code.accrual_code_id}>
                                {code.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {/* Cantidad fija */}
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                    fullWidth
                    name="fixedAmount"
                    label={t("accruals.balance_domino.dialog.inputs.fixed_amount")}
                    value={data.fixed_amount}
                    type="number"
                    autoComplete='off'
                    onChange={(e) => {
                        setData({ ...data, fixed_amount: Number(e.target.value) });
                    }}
                />
            </Grid>

            {/* Porcentaje */}
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <TextField
                    fullWidth
                    name="percentageCascade"
                    label={t("accruals.balance_domino.dialog.inputs.percentage_cascade")}
                    value={data.percentage_cascade}
                    type="number"
                    autoComplete='off'
                    onChange={(e) => {
                        setData({ ...data, percentage_cascade: Number(e.target.value) });
                    }}
                />
            </Grid>

            {/* Tipo cascada */}
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <FormControl fullWidth>
                    <InputLabel>{t("accruals.balance_domino.dialog.inputs.type_cascade")}</InputLabel>
                    <Select
                        value={data.type_cascade}
                        label={t("accruals.balance_domino.dialog.inputs.type_cascade")}
                        onChange={(e) => {
                            setData({ ...data, type_cascade: e.target.value as TipoCascadaEnum });
                        }}
                    >
                        <MenuItem value={TipoCascadaEnum.REDUCIR_A_CERO}>{t("accruals.balance_domino.dialog.inputs.type_cascade_options.reduce_to_zero")}</MenuItem>
                        <MenuItem value={TipoCascadaEnum.INCREMENTAR_A_CERO}>{t("accruals.balance_domino.dialog.inputs.type_cascade_options.increment_to_zero")}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            {/* Sección: Orden en cascada */}
            <Grid size={{ xs: 12 }}>
                <Box sx={{ mt: 3, mb: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
                    <strong>{t("accruals.balance_domino.dialog.sections.cascade_order")}</strong>
                </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <CascadeOrderList
                    typeAccrual={data.type_accrual}
                    value={data.cascade_orders || []}
                    onChange={(items) => setData({ ...data, cascade_orders: items })}
                />
            </Grid>
        </Grid>
    );
};
